import React, { useEffect, useState } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { UnsavedChangesModal } from '@vlabs/monokit/features/unsaved-changes-modal';
import {
  Page, Divider, Control, openConfirmPopup, Breadcrumbs, Breadcrumb,
} from '@vlabs/uikit';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { StatusLabelCell } from './components/cells/Cell';
import { VideoAnalyticsForm } from './components/forms/VideoAnalyticsForm';
import st from './VideoAnalyticsStreamPage.module.sass';

function VideoAnalyticsStreamPage({ streamId }) {
  const { t } = useTranslation();
  const history = useHistory();
  const [streamStatus, setStreamStatus] = useState(null);
  const [formData, setFormData] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [isError, setIsError] = useState(false);
  const methods = useForm({ defaultValues: {}, values: formData });

  const streamTitle = formData?.name || t('videoAnalytics:editAndCreateStreamsPageTitles.untitled_stream');

  const formContext = {
    ...methods,
    availableGroups,
  };

  const onSubmit = async (data) => {
    await apiContainer.lunaClient.streams.edit({ streamId, payload: data });
    toast.success(t('videoAnalytics:edit.success'));
    // сброс defaultValues чтобы вернуть isDirty в false
    methods.reset(data);
  };
  const onCancel = () => history.push('/video-analytics');
  const onInvalid = () => toast.error(t('videoAnalytics:error.invalid_form_data'));

  useEffect(() => {
    (async () => {
      const getGroupsRequest = apiContainer.lunaClient.groups.getAll;
      const getStreamDataRequest = apiContainer.lunaClient.streams.get;

      const formDataResponse = await Promise.allSettled([
        getGroupsRequest(),
        getStreamDataRequest(streamId),
      ]);

      const [
        { status: groupsStatus, value: groupsData },
        { status: streamDataStatus, value: streamData },
      ] = formDataResponse;

      if (streamDataStatus === 'rejected') {
        setIsError(true);
        return;
      }

      let groups = [];

      if (groupsStatus === 'fulfilled' && groupsData.length) {
        groups = groupsData.map(({ group_id, group_name }) => ({ value: group_id, label: group_name }));
        setAvailableGroups(groups);
      }

      setStreamStatus(streamData.status);
      setFormData({
        ...streamData,
        group_name: streamData.groups ? groups.find((option) => option.value === streamData.groups[0]) : null,
      });
    })();
  }, []);

  const onDeleteStream = () => {
    const onConfirm = async () => {
      // сброс defaultValues чтобы вернуть isDirty в false
      methods.reset();
      await apiContainer.lunaClient.streams.delete(streamId);
      toast.success(t('videoAnalytics:buttons.removing_done_stream_message'));
      onCancel();
    };

    openConfirmPopup({
      title: t('videoAnalytics:buttons.removing_stream'),
      message: t('videoAnalytics:buttons.removing_stream_message'),
      type: 'delete',
      onConfirm,
      confirmButtonProps: {
        'data-testid': 'videoanalytics.confirmDeleteButton',
      },
    }, { className: st.RemovePopup });
  };

  const actions = (
    <div className={st.Actions}>
      <Control.Button
        data-testid="saveStreamButton"
        type="submit"
      >
        {t('videoAnalytics:buttons.save')}
      </Control.Button>

      <Control.Button
        data-testid="deleteStreamsButton"
        kind="negative"
        onClick={onDeleteStream}
        type="button"
        variant="filled"
      >
        {t('videoAnalytics:buttons.delete')}
      </Control.Button>
    </div>
  );

  const breadcrumbs = (
    <Breadcrumbs>
      <Breadcrumb
        caption={t('videoAnalytics:table_header.videoanalytics')}
        href="/video-analytics"
      />
      <Breadcrumb caption={streamTitle} />
    </Breadcrumbs>
  );

  if (isError) {
    return (
      <div className={st.Error}>
        <h4 className={st.Error__Content}>
          {t('videoAnalytics:error.failed_load_data')}
        </h4>
      </div>
    );
  }

  return (
    <FormProvider {...formContext}>
      <UnsavedChangesModal when={!!methods.formState.isDirty && !methods.formState.isSubmitting} />
      <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
        <Page
          actions={actions}
          breadcrumbs={breadcrumbs}
          className={st.Root}
          status={<StatusLabelCell value={streamStatus} />}
          title={streamTitle}
        >
          <Divider />
          <VideoAnalyticsForm />
        </Page>
      </form>
    </FormProvider>
  );
}

export default VideoAnalyticsStreamPage;
